/**
 * Robuuste extractie van de post-tekst uit AI-output.
 *
 * Het model levert de caption soms als kale tekst, soms als
 * { "content": "..." }, soms in een ```json ... ``` codeblok, soms met
 * wat uitleg eromheen. Eerdere versie deed alleen JSON.parse() en viel
 * bij de minste afwijking terug op de RUWE string — waardoor de wrapper
 * + letterlijke \n in social_media_posts.post_content belandden.
 *
 * Deze helper haalt er altijd de schone tekst uit en gooit nooit.
 * Wordt gebruikt bij het aanmaken (insert) én bij het laden in de
 * editor (self-healing voor reeds kapotte drafts).
 */
export function extractPostContent(input: unknown): string {
  if (input == null) return '';

  // Al een object met content
  if (typeof input === 'object') {
    const obj = input as Record<string, unknown>;
    if (typeof obj.content === 'string') return obj.content.trim();
    return String(input);
  }

  let text = String(input).trim();

  // Markdown codefences strippen (```json ... ``` of ``` ... ```)
  const fence = text.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  if (fence) text = fence[1].trim();

  // Directe JSON-parse
  const fromJson = tryExtractJson(text);
  if (fromJson !== null) return fromJson;

  // Eerste {...}-blok eruit vissen (model zette er proza omheen)
  const brace = text.match(/\{[\s\S]*\}/);
  if (brace) {
    const fromBlock = tryExtractJson(brace[0]);
    if (fromBlock !== null) return fromBlock;
  }

  // Laatste redmiddel: het model wrapte in {"content":"..."} maar de JSON is
  // onparseerbaar. Dat gebeurt vooral bij TRUNCATIE (max_tokens bereikt): de
  // envelope mist dan de afsluitende "} — vandaar dat een strikte match op een
  // sluitquote faalde en de rauwe wrapper lekte. Pak daarom alles ná de
  // openingsquote van "content", strip een eventuele afsluitende "}/losse
  // backslash van een half-afgekapte escape, en de-escape.
  const open = text.match(/"content"\s*:\s*"/);
  if (open) {
    let inner = text.slice(open.index! + open[0].length);
    inner = inner.replace(/"\s*\}?\s*$/, ''); // complete of half-afgesloten envelope
    inner = inner.replace(/\\+$/, ''); // dangling backslash van een afgekapte \n
    return inner
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\')
      .trim();
  }

  return text;
}

function tryExtractJson(text: string): string | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }

  // Dubbel-encoded: een JSON-string die zelf weer JSON is
  if (typeof parsed === 'string') {
    const inner: string = parsed;
    try {
      parsed = JSON.parse(inner);
    } catch {
      return inner.trim();
    }
  }

  if (parsed && typeof parsed === 'object') {
    const c = (parsed as Record<string, unknown>).content;
    if (typeof c === 'string') return c.trim();
  }
  if (typeof parsed === 'string') return parsed.trim();
  return null;
}
